/** @jsx isolateComponent */
import { css } from '@emotion/react';
import type { MouseEvent as ReactMouseEvent } from 'react';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
/* eslint-disable import/no-duplicates */
import { Animation, Colors, Icon, TooltipWrapper } from '@zapier/design-system';
import type { IconName } from '@zapier/design-system';

type Props = {
  /**
   * ARIA attribute indicating whether or not the content
   * controlled by the button is visible.
   */
  ariaExpanded?: boolean;
  /**
   * ARIA attribute to label the button if `label` is empty.
   */
  ariaLabel?: string;
  /**
   * Name of the `Icon` to render.
   */
  icon?: IconName;
  /**
   *  Indicates what the button does. Used for the `ariaLabel` on the
   * `Button` as well as the text for the `Tooltip`.
   * This prop should be supplied in most circumstances, but in cases
   * **when it's not desired be sure to pass `ariaLabel`!**
   */
  label?: string;
  /**
   * Callback to be executed when the button is clicked.
   */
  onClick?: (e: ReactMouseEvent) => void;
};

const Styles = labelStyles('MenuCollapseButton', {
  root: css`
    width: 44px;
    height: 44px;
    border-radius: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${Colors.neutral600};
    transition: ${Animation.transitionValue};

    &:hover,
    &:focus {
      background-color: none;
      color: ${Colors.neutral800};
    }
  `,
});

/**
 * @private
 * `MenuCollapseButton` renders button to toggle whether the sidebar is open or closed.
 */
const MenuCollapseButton = ({
  ariaExpanded = false,
  ariaLabel = undefined,
  icon = 'navMenu',
  label = 'Menu',
  onClick = undefined,
}: Props) => {
  return (
    <TooltipWrapper
      content={label}
      position="east"
      shouldCorrectPosition={false}
      showTooltipOnTouchDevices={false}
    >
      {() => (
        <button
          type="button"
          aria-expanded={ariaExpanded}
          aria-label={ariaLabel || label}
          css={Styles.root}
          onClick={(event: ReactMouseEvent) => {
            if (onClick) {
              onClick(event);
            }
          }}
        >
          <Icon name={icon} size={24} />
        </button>
      )}
    </TooltipWrapper>
  );
};

export default MenuCollapseButton;
